// src/features/fx-ranking/components/CotPopup.tsx
//
// Floating COT-positioning pop-over for a single FX currency. Opened by
// clicking a ticker in the universe table; fetches the weekly Commercial
// (hedger) vs Non-Commercial (speculator) net-position history from
// GET /api/v1/fx-ranking/cot/<symbol>.
//
// FX sibling of features/commodities-ranking/components/CotPopup. The
// data shape is identical (same CFTC schema); only the empty-state copy
// differs (currencies without CFTC coverage are the EM Tier-3 names).

import { useEffect, useState } from 'react';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { fetchCotHistory, type CotHistoryResponse } from '../api';

const formatContracts = (value: number | null): string => {
    if (value == null || !Number.isFinite(value)) return 'N/A';
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}k`;
    return `${sign}${abs.toFixed(0)}`;
};

interface CotPopupProps {
    symbol: string;
    onClose: () => void;
}

const CotTooltip = ({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: { name: string; value: number; color: string }[];
    label?: string;
}) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
            <p className="font-bold text-slate-900 dark:text-white">{label}</p>
            {payload.map((p) => (
                <p key={p.name} style={{ color: p.color }}>
                    {p.name}:{' '}
                    <span className="font-mono">
                        {formatContracts(p.value)}
                    </span>
                </p>
            ))}
        </div>
    );
};

// Presentational COT chart + summary stats body, decoupled from the modal
// shell so it can be embedded both in the standalone <CotPopup/> and as a tab
// inside <FXDetailPopup/>. Takes already-fetched state (loading/error/data).
interface CotChartBodyProps {
    symbol: string;
    data: CotHistoryResponse | null;
    isLoading: boolean;
    error: string | null;
}

export const CotChartBody = ({
    symbol,
    data,
    isLoading,
    error,
}: CotChartBodyProps) => {
    const series = data?.series ?? [];
    const latest = series.length > 0 ? series[series.length - 1] : null;

    return (
        <>
            {isLoading && (
                <div className="space-y-3" aria-busy="true">
                    <div className="h-64 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
                </div>
            )}

            {error && !isLoading && (
                <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {data && !isLoading && !error && series.length === 0 && (
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-3 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                    No CFTC COT positioning history is available for this
                    currency. Only the 11 G10 / major-EM currencies are covered
                    by the CFTC CME report.
                </div>
            )}

            {data && !isLoading && !error && series.length > 0 && (
                <>
                    <div
                        className="w-full h-64 text-slate-500 dark:text-slate-400"
                        role="img"
                        aria-label={`${symbol} net positioning history`}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={series}
                                margin={{
                                    top: 8,
                                    right: 16,
                                    bottom: 8,
                                    left: 8,
                                }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    className="stroke-slate-200 dark:stroke-slate-700"
                                />
                                <XAxis
                                    dataKey="date"
                                    tick={{
                                        fontSize: 10,
                                        fill: 'currentColor',
                                    }}
                                    tickLine={false}
                                    minTickGap={40}
                                />
                                <YAxis
                                    tick={{
                                        fontSize: 10,
                                        fill: 'currentColor',
                                    }}
                                    tickLine={false}
                                    width={48}
                                    tickFormatter={formatContracts}
                                />
                                <Tooltip content={<CotTooltip />} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Line
                                    type="monotone"
                                    name="Commercials"
                                    dataKey="net_commercial"
                                    stroke="#0ea5e9"
                                    strokeWidth={2}
                                    dot={false}
                                />
                                <Line
                                    type="monotone"
                                    name="Speculators"
                                    dataKey="net_noncomm"
                                    stroke="#10b981"
                                    strokeWidth={2}
                                    dot={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {latest && (
                        <dl className="mt-4 grid grid-cols-2 gap-3">
                            <div className="rounded-lg border border-slate-100 dark:border-slate-800 px-4 py-3">
                                <dt className="text-xs text-slate-500 dark:text-slate-400">
                                    Net Commercials (latest)
                                </dt>
                                <dd className="font-mono font-semibold text-sky-600 dark:text-sky-400">
                                    {formatContracts(latest.net_commercial)}
                                </dd>
                            </div>
                            <div className="rounded-lg border border-slate-100 dark:border-slate-800 px-4 py-3">
                                <dt className="text-xs text-slate-500 dark:text-slate-400">
                                    Net Speculators (latest)
                                </dt>
                                <dd className="font-mono font-semibold text-green-600 dark:text-emerald-400">
                                    {formatContracts(latest.net_noncomm)}
                                </dd>
                            </div>
                        </dl>
                    )}
                </>
            )}
        </>
    );
};

// Self-contained data hook so both <CotPopup/> and <FXDetailPopup/> can fetch
// COT history with identical loading / error semantics.
export const useCotHistory = (symbol: string, days = 365) => {
    const [data, setData] = useState<CotHistoryResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        setData(null);
        (async () => {
            try {
                const res = await fetchCotHistory(symbol, days);
                if (!cancelled) setData(res);
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load COT history', err);
                setError('Unable to load positioning data for this currency.');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [symbol, days]);

    return { data, isLoading, error };
};

export const CotPopup = ({ symbol, onClose }: CotPopupProps) => {
    const { data, isLoading, error } = useCotHistory(symbol, 365);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="relative w-full max-w-2xl rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`${symbol} COT positioning`}
            >
                <button
                    type="button"
                    className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ✕
                </button>

                <h2 className="text-2xl font-bold text-slate-900 dark:text-white pr-8">
                    {data?.name ?? symbol}
                </h2>
                <p className="mt-0.5 text-sm font-medium text-green-600 dark:text-emerald-400">
                    {symbol}
                    {data?.cot_symbol && (
                        <span className="ml-2 rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                            CFTC {data.cot_symbol}
                        </span>
                    )}
                </p>
                <p className="mt-1 mb-5 text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    Net Positioning · 52 weeks
                </p>

                <CotChartBody
                    symbol={symbol}
                    data={data}
                    isLoading={isLoading}
                    error={error}
                />
            </div>
        </div>
    );
};

export default CotPopup;
